import { useEffect, useReducer, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Seo from "@/components/Seo";

const STORAGE_KEY = "counselor-marketing-plan-v1";

type State = Record<string, string>;

const initialState: State = {
  email: "",
  niche_character: "",
  niche_problem: "",
  niche_plan: "",
  niche_success: "",
  plan_niche: "",
  plan_preacher_1: "",
  plan_preacher_2: "",
  plan_preacher_3: "",
  plan_talk: "",
  plan_inquiry: "",
  plan_offboarding: "",
  plan_directory_1: "",
  plan_directory_2: "",
  plan_directory_3: "",
  action_one_thing: "",
  action_done: "",
  action_date: "",
};

function reducer(state: State, action: { name: string; value: string }): State {
  return { ...state, [action.name]: action.value };
}

function buildNiche(s: State) {
  return {
    character: s.niche_character,
    problem: s.niche_problem,
    plan: s.niche_plan,
    success: s.niche_success,
  };
}

function WorksheetField({
  number,
  title,
  caption,
  name,
  value,
  onChange,
  rows = 3,
}: {
  number: string;
  title: string;
  caption?: string;
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  rows?: number;
}) {
  return (
    <div className="mb-10">
      <div className="flex items-baseline gap-3 mb-1">
        <span className="font-georgia italic text-gray-400 text-lg">{number}</span>
        <h3 className="font-bold text-base text-black">{title}</h3>
      </div>
      {caption && (
        <p className="font-georgia italic text-gray-500 text-sm mb-2 ml-9">
          {caption}
        </p>
      )}
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className="w-full ml-0 bg-transparent border-0 border-b border-[#111] focus:outline-none focus:border-[#0E2644] resize-none py-2 text-black text-base font-sans"
      />
    </div>
  );
}

function SingleLine({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
}) {
  return (
    <div className="flex items-baseline gap-3 mb-3">
      <span className="font-georgia italic text-gray-400 w-6 shrink-0">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className="flex-1 bg-transparent border-0 border-b border-[#111] focus:outline-none focus:border-[#0E2644] py-1 text-black text-base font-sans"
      />
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-500 uppercase tracking-[0.2em] text-xs font-sans mb-4">
      {children}
    </p>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-georgia italic text-4xl md:text-5xl text-black mb-4 leading-tight">
      {children}
    </h2>
  );
}

const DownloadButton = ({ state }: { state: State }) => {
  const [loading, setLoading] = useState(false);
  const recordedRef = useRef(false);

  const recordDownload = async () => {
    if (recordedRef.current) return;
    recordedRef.current = true;
    try {
      await supabase.from("counselor_plan_downloads").insert({
        email: state.email || null,
        plan_data: state,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch (e) {
      console.error("Failed to record download", e);
    }
  };

  const handleDownload = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const [{ pdf }, { default: PlanPDF }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("@/components/PlanPDF"),
      ]);
      const blob = await pdf(<PlanPDF state={state} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "counselor-marketing-plan.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      recordDownload();
    } catch (e) {
      console.error("PDF generation failed", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={loading}
      className="inline-block bg-[#0E2644] text-white px-6 py-3 text-sm font-sans font-medium rounded-sm hover:bg-[#0E2644]/90 transition-colors disabled:opacity-60"
    >
      {loading ? "Preparing PDF…" : "Download as PDF"}
    </button>
  );
};


export default function CounselorMarketingPlan() {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Hydrate from localStorage (initial + cross-tab + on tab focus)
  useEffect(() => {
    const hydrate = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        Object.entries(parsed).forEach(([name, value]) => {
          if (typeof value === "string") {
            dispatch({ name, value });
          }
        });
      } catch {}
    };

    hydrate();

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) hydrate();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") hydrate();
    };

    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", hydrate);

    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", hydrate);
    };
  }, []);

  // Save
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  const onChange = (name: string, value: string) => dispatch({ name, value });

  const niche = buildNiche(state);
  const placeholderClass = "text-gray-400";
  const filledClass = "text-black";

  // Auto-populate plan_niche when empty from niche
  useEffect(() => {
    if (!state.plan_niche) {
      const auto = nicheSentence(niche);
      if (auto && (niche.character || niche.problem || niche.plan || niche.success)) {
        // don't overwrite if user typed; only seed if blank
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      {/* GENERATED META — review/refine title + description. */}
      <Seo
        title="Counselor Marketing Plan | Blue Heron Marketing"
        description="A free, fill-in-the-blank marketing plan worksheet for counselors and therapists to attract more of the right clients."
        path="/counselor-marketing-plan"
      />
      <style dangerouslySetInnerHTML={{ __html: `
        .font-georgia { font-family: Georgia, 'Times New Roman', serif; }
        .font-sans { font-family: Inter, system-ui, -apple-system, sans-serif; }
      ` }} />

      <div className="max-w-[720px] mx-auto px-6 py-16 md:py-24">
        {/* Hero */}
        <section className="mb-24">
          <Eyebrow>Marketing Seminar Worksheet</Eyebrow>
          <h1 className="font-georgia italic text-5xl md:text-6xl text-black leading-tight mb-6">
            Your counselor marketing plan
          </h1>
          <p className="text-lg text-black mb-2">Built during the seminar. Use it anytime.</p>
          

          <div className="mb-8 max-w-md">
            <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
              Your email
            </label>
            <input
              type="email"
              value={state.email}
              onChange={(e) => onChange("email", e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent border-0 border-b border-[#111] focus:outline-none focus:border-[#0E2644] py-2 text-black text-base font-sans placeholder:text-gray-400"
            />
          </div>

          <DownloadButton state={state} />
        </section>

        {/* Section 1 */}
        <section className="mb-24">
          <Eyebrow>StoryBrand Exercise</Eyebrow>
          <Heading>Make it specific. Make it stick.</Heading>
          <p className="text-base text-black mb-10 leading-relaxed">
            Donald Miller's StoryBrand formula. Fill in the four blanks. Your client is
            the hero. You're the guide.
          </p>

          <WorksheetField
            number="01"
            title="Character / Customer"
            caption="Who is your ideal client? Be specific. Not 'people who need counseling.'"
            name="niche_character"
            value={state.niche_character}
            onChange={onChange}
          />
          <WorksheetField
            number="02"
            title="Problem"
            caption="What are they struggling with?"
            name="niche_problem"
            value={state.niche_problem}
            onChange={onChange}
          />
          <WorksheetField
            number="03"
            title="Plan / How you help"
            caption="What's your approach?"
            name="niche_plan"
            value={state.niche_plan}
            onChange={onChange}
          />
          <WorksheetField
            number="04"
            title="Success / Outcome"
            caption="What does life look like for them after?"
            name="niche_success"
            value={state.niche_success}
            onChange={onChange}
          />

          <div className="flex items-center gap-4 my-10">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-[#0E2644] uppercase tracking-[0.2em] text-xs font-sans font-semibold">
              Now Combine Them
            </span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          <div className="bg-[#F4F4F4] border-l-4 border-[#0E2644] p-6 mb-4">
            <p className="font-georgia italic text-xl leading-relaxed">
              Most{" "}
              <span className={niche.character ? filledClass : placeholderClass}>
                {niche.character || "[character]"}
              </span>{" "}
              struggle with{" "}
              <span className={niche.problem ? filledClass : placeholderClass}>
                {niche.problem || "[problem]"}
              </span>
              . I help them{" "}
              <span className={niche.plan ? filledClass : placeholderClass}>
                {niche.plan || "[plan]"}
              </span>
              . The result is{" "}
              <span className={niche.success ? filledClass : placeholderClass}>
                {niche.success || "[success]"}
              </span>
              .
            </p>
          </div>
          <p className="font-georgia italic text-gray-500 text-sm">
            If a colleague reads this, they should immediately know who to send your way.
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-24">
          <Eyebrow>Six-Item Plan</Eyebrow>
          <Heading>Six commitments. One quarter.</Heading>
          <p className="text-base text-black mb-10 leading-relaxed">
            Adapt to fit your time and style. Skip what doesn't apply.
          </p>

          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="font-georgia italic text-gray-400 text-lg">01</span>
              <h3 className="font-bold text-base text-black">My niche in one sentence</h3>
            </div>
            <textarea
              rows={2}
              value={state.plan_niche || nicheSentence(niche)}
              onChange={(e) => onChange("plan_niche", e.target.value)}
              className="w-full bg-transparent border-0 border-b border-[#111] focus:outline-none focus:border-[#0E2644] resize-none py-2 text-black text-base font-sans"
            />
          </div>

          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="font-georgia italic text-gray-400 text-lg">02</span>
              <h3 className="font-bold text-base text-black">3 preachers I'll meet this quarter</h3>
            </div>
            <p className="font-georgia italic text-gray-500 text-sm mb-3 ml-9">
              Name, church, why you want to connect.
            </p>
            <SingleLine label="1." name="plan_preacher_1" value={state.plan_preacher_1} onChange={onChange} />
            <SingleLine label="2." name="plan_preacher_2" value={state.plan_preacher_2} onChange={onChange} />
            <SingleLine label="3." name="plan_preacher_3" value={state.plan_preacher_3} onChange={onChange} />
          </div>

          <WorksheetField
            number="03"
            title="My one quarterly talk"
            caption="Topic and audience."
            name="plan_talk"
            value={state.plan_talk}
            onChange={onChange}
          />
          <WorksheetField
            number="04"
            title="My inquiry response system"
            caption="Response time, template, booking link."
            name="plan_inquiry"
            value={state.plan_inquiry}
            onChange={onChange}
          />
          <WorksheetField
            number="05"
            title="My client offboarding ritual"
            caption="Closing note, review ask, referral path."
            name="plan_offboarding"
            value={state.plan_offboarding}
            onChange={onChange}
          />

          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="font-georgia italic text-gray-400 text-lg">06</span>
              <h3 className="font-bold text-base text-black">Directories I'll apply to this month</h3>
            </div>
            <div className="mt-3">
              <SingleLine label="1." name="plan_directory_1" value={state.plan_directory_1} onChange={onChange} />
              <SingleLine label="2." name="plan_directory_2" value={state.plan_directory_2} onChange={onChange} />
              <SingleLine label="3." name="plan_directory_3" value={state.plan_directory_3} onChange={onChange} />
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section className="mb-24">
          <Eyebrow>30-Day Commitment</Eyebrow>
          <Heading>Pick one. Get specific. Put a date on it.</Heading>
          <p className="text-base text-black mb-10 leading-relaxed">
            Choose the one thing from above you'll actually do in the next 30 days.
          </p>

          <WorksheetField
            number="01"
            title="The ONE thing I'll do in the next 30 days"
            name="action_one_thing"
            value={state.action_one_thing}
            onChange={onChange}
          />
          <WorksheetField
            number="02"
            title="How I'll know it's done"
            name="action_done"
            value={state.action_done}
            onChange={onChange}
          />

          <div className="mb-10">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="font-georgia italic text-gray-400 text-lg">03</span>
              <h3 className="font-bold text-base text-black">My check-in date</h3>
            </div>
            <input
              type="date"
              value={state.action_date}
              onChange={(e) => onChange("action_date", e.target.value)}
              className="w-full bg-transparent border-0 border-b border-[#111] focus:outline-none focus:border-[#0E2644] py-2 text-black text-base font-sans"
            />
          </div>

        </section>

        {/* Footer */}
        <footer className="pt-10 border-t border-gray-300">
          <p className="text-sm text-gray-600 mb-8">
            Luke Adams · Blue Heron Marketing · luke@blueheronmarketing.com
          </p>
          <DownloadButton state={state} />
        </footer>
      </div>
    </div>
  );
}

function nicheSentence(n: { character: string; problem: string; plan: string; success: string }) {
  if (!n.character && !n.problem && !n.plan && !n.success) return "";
  return `Most ${n.character || "[character]"} struggle with ${n.problem || "[problem]"}. I help them ${n.plan || "[plan]"}. The result is ${n.success || "[success]"}.`;
}
